import classNames from 'classnames';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCreateGroupMutation, useGetRoleGroupsQuery } from '../../graph/services/graphApi';
import CreationActions from '../../groups/components/CreationActions';
import CreationStatus from '../../groups/components/CreationStatus';
import {
  toMailNickname,
  useGroupCreation,
  NAME_SUFFIX_ERROR,
  NAME_SUFFIX_HELP,
  NAME_SUFFIX_PATTERN,
  type CreationStep,
} from '../../groups/groupCreation';

const ROLE_PREFIX = 'Role_';

// The Role creation form body. The button + modal shell live in CreateRoleGroup; this
// renders the name/description inputs, a live preview, the step status, and actions, then
// calls onClose on cancel or after a successful run finishes navigating to the new role.
// Roles never sync to on-prem AD, so there is no writeback option.
export default function RoleForm({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [nameSuffix, setNameSuffix] = useState('');
  const [description, setDescription] = useState('');

  const { data: roleGroups } = useGetRoleGroupsQuery();
  const [createGroup] = useCreateGroupMutation();
  const { steps, isRunning, done, failed, execute } = useGroupCreation();

  const displayName = `${ROLE_PREFIX}${nameSuffix}`;

  // Validation
  const hasInvalidChars = !NAME_SUFFIX_PATTERN.test(nameSuffix);
  const nameValid = nameSuffix.trim().length > 0 && !hasInvalidChars;
  const alreadyExists = !!roleGroups?.some((g) => g.displayName === displayName);
  const canCreate = nameValid && !alreadyExists;

  const handleCreate = () => {
    let roleGroupId = '';

    const creationSteps: CreationStep[] = [
      {
        errorFallback: 'Failed to create Role group',
        label: 'Create Role group',
        run: async () => {
          const roleGroup = await createGroup({
            // Leave description off entirely when blank rather than posting an empty string.
            description: description.trim() ? description : undefined,
            displayName,
            mailNickname: toMailNickname(displayName),
          }).unwrap();
          roleGroupId = roleGroup.id;
        },
      },
    ];

    execute(creationSteps, () => {
      onClose();
      navigate(`/roles/${roleGroupId}`);
    });
  };

  return (
    <>
      <div className="font-bold text-xl mb-4">Create Role group</div>

      {!isRunning && !done && !failed && (
        <>
          {/* Name input with fixed Role_ prefix */}
          <div className="mb-4">
            <label htmlFor="role-name" className="block font-bold mb-1 text-sm">
              Group name
            </label>
            <div className="flex items-center">
              <span className="font-mono text-sm bg-base-200 rounded-l-lg border border-r-0 border-base-300 px-3 py-2">
                {ROLE_PREFIX}
              </span>
              <input
                id="role-name"
                type="text"
                className={classNames('input input-bordered w-full rounded-l-none', {
                  'input-error': hasInvalidChars,
                })}
                placeholder="ANZ_Project_Contributor"
                value={nameSuffix}
                onChange={(e) => setNameSuffix(e.target.value)}
              />
            </div>
            {hasInvalidChars ? (
              <div className="text-error text-xs mt-1">{NAME_SUFFIX_ERROR}</div>
            ) : (
              <div className="text-base-content/60 text-xs mt-1">{NAME_SUFFIX_HELP}</div>
            )}
          </div>

          {/* Description input (optional) */}
          <div className="mb-4">
            <label htmlFor="role-description" className="block font-bold mb-1 text-sm">
              Description <span className="font-normal text-base-content/60">(optional)</span>
            </label>
            <input
              id="role-description"
              type="text"
              className="input input-bordered w-full"
              placeholder="What this role is for"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Live preview */}
          {nameValid && (
            <div className="mb-4 bg-base-200 rounded-lg p-3 text-sm">
              <div className="font-bold mb-2">Group to be created:</div>
              <div className="font-mono text-xs mb-1">{displayName}</div>
              {description.trim().length > 0 && (
                <div className="text-base-content/70 text-xs mt-2 italic">{description}</div>
              )}
            </div>
          )}

          {/* Duplicate warning */}
          {alreadyExists && (
            <div className="alert alert-error mb-4 text-sm py-2">
              A Role group named <strong className="mx-1">{displayName}</strong> already exists.
            </div>
          )}
        </>
      )}

      {(isRunning || done || failed) && (
        <CreationStatus
          steps={steps}
          done={done}
          successMessage="Role group created — navigating to it…"
        />
      )}

      {!done && (
        <CreationActions
          busy={isRunning}
          failed={failed}
          canConfirm={canCreate}
          confirmLabel="Create"
          onClose={onClose}
          onConfirm={handleCreate}
        />
      )}
    </>
  );
}
